import type { NavigationProp } from '@react-navigation/native';
import { useLocalSearchParams, useNavigation } from 'expo-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { NativeScrollEvent, NativeSyntheticEvent, ScrollView } from 'react-native';

import { useIsDesktopBp } from '@fhs-legacy/frontend/src/hooks/breakpoints';
import { useAuthContext } from '@fhs-legacy/frontend/src/state/auth';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';

import { useActiveChallengesBadge } from '../../hooks/use-active-challenges-badge';
import { useLoyaltyLegacyStates } from '../../hooks/use-loyalty-legacy-states';
import { useChallengesUi, useLoyaltyIncentives } from '../../queries/loyalty.queries';

export type Section = 'challenges' | 'offers' | 'rewards';

type RewardsParamList = {
  index: { section?: Section };
};

type ScrollRequest = {
  section: Section;
  animated: boolean;
  y?: number;
};

export const useScrollManager = (onTabReentry?: () => void) => {
  const { section } = useLocalSearchParams<RewardsParamList['index']>();
  const navigation = useNavigation<NavigationProp<RewardsParamList, 'index'>>();
  const [activeSection, setActiveSection] = useState<Section>(section ?? 'challenges');
  const scrollViewRef = useRef<ScrollView | null>(null);
  const sectionPositions = useRef<Partial<Record<Section, number>>>({});
  const scrollRequest = useRef<ScrollRequest>();
  const frameScheduled = useRef(false);
  const sectionLinkHandled = useRef(false);

  const applyPendingScroll = useCallback(() => {
    if (!scrollRequest.current) {
      return;
    }
    scrollRequest.current.y = undefined;
    if (frameScheduled.current) {
      return;
    }

    // Combine layout updates into one scroll using the latest measured position.
    frameScheduled.current = true;
    requestAnimationFrame(() => {
      frameScheduled.current = false;
      const request = scrollRequest.current;
      const y =
        request &&
        (request.section === 'challenges' ? 0 : sectionPositions.current[request.section]);
      if (request && y !== undefined && scrollViewRef.current) {
        request.y = y;
        scrollViewRef.current.scrollTo({ y, animated: request.animated });
      }
    });
  }, []);

  // Select immediately; layout callbacks apply the request if content is not ready yet.
  const scrollToSection = useCallback(
    (target: Section, animated = true) => {
      setActiveSection(target);
      scrollRequest.current = { section: target, animated };
      applyPendingScroll();
    },
    [applyPendingScroll]
  );

  // Scroll to a linked section once. Clearing the parameter makes the next run a no-op,
  // and updating the ref does not trigger a render.
  useEffect(() => {
    if (section) {
      sectionLinkHandled.current = true;
      scrollToSection(section, false);
      navigation.setParams({ section: undefined });
    }
  }, [section, navigation, scrollToSection]);

  // Reload/reset only when returning from another tab; closing a modal keeps the view.
  // Unchanged tab keys do nothing. Stable dependencies keep one listener, cleaned up below.
  useEffect(() => {
    const tabs = navigation.getParent();
    if (!tabs || tabs.getState().type !== 'tab') {
      return;
    }

    const initialState = tabs.getState();
    let previousTab = initialState.routes[initialState.index].key;

    return tabs.addListener('state', () => {
      const state = tabs.getState();
      const selectedTab = state.routes[state.index].key;
      if (selectedTab === previousTab) {
        return;
      }
      previousTab = selectedTab;

      // A modal changes parent focus, but not the selected tab. Only a real
      // tab re-entry resets the view; an explicit section link takes precedence.
      if (navigation.isFocused()) {
        onTabReentry?.();
        if (!sectionLinkHandled.current) {
          scrollToSection('challenges', false);
        }
      }
      sectionLinkHandled.current = false;
    });
  }, [navigation, scrollToSection, onTabReentry]);

  const attachScrollView = useCallback(
    (view: ScrollView | null) => {
      scrollViewRef.current = view;
      if (view && scrollRequest.current) {
        setActiveSection(scrollRequest.current.section);
      }
      applyPendingScroll();
    },
    [applyPendingScroll]
  );

  function onScroll(event: NativeSyntheticEvent<NativeScrollEvent>) {
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    const request = scrollRequest.current;
    if (request) {
      // Keep the selected tab during animation, including a target near the page bottom.
      if (
        request.y !== undefined &&
        contentSize.height > request.y &&
        layoutMeasurement.height > 0
      ) {
        const targetY = Math.min(
          request.y,
          Math.max(0, contentSize.height - layoutMeasurement.height)
        );
        if (Math.abs(contentOffset.y - targetY) <= 1) {
          scrollRequest.current = undefined;
        }
      }
      return;
    }

    const y = contentOffset.y + 40;
    const positions = sectionPositions.current;
    setActiveSection(
      positions.rewards !== undefined && y >= positions.rewards
        ? 'rewards'
        : positions.offers !== undefined && y >= positions.offers
        ? 'offers'
        : 'challenges'
    );
  }

  function onScrollBeginDrag(event: NativeSyntheticEvent<NativeScrollEvent>) {
    // A manual drag takes over from any pending tab navigation.
    scrollRequest.current = undefined;
    onScroll(event);
  }

  function setSectionPosition(target: Section, y: number) {
    sectionPositions.current[target] = y;
    if (scrollRequest.current?.section === target) {
      applyPendingScroll();
    }
  }

  return {
    scrollViewRef: attachScrollView,
    activeSection,
    scrollToSection,
    onScroll,
    onScrollBeginDrag,
    setSectionPosition,
    onContentSizeChange: applyPendingScroll,
  };
};

export const useIsLoyaltyPageLoading = () => {
  const { loyaltyUserReady, loyaltyUser } = useLoyaltyLegacyStates();

  const { isLoading: isChallengesLoading } = useChallengesUi();
  const { isLoading: isLoyaltyIncentivesLoading } = useLoyaltyIncentives();

  return isLoyaltyIncentivesLoading || (!loyaltyUserReady && !loyaltyUser) || isChallengesLoading;
};

export const useShouldHideChallenges = () => {
  const { isAuthenticated } = useAuthContext();
  const isChallengeUiEnabled = useFlag('enable-challenge-ui');
  const { data: challengesData, isPending } = useChallengesUi();

  return (
    !isChallengeUiEnabled ||
    !isAuthenticated ||
    (challengesData && challengesData.length === 0) ||
    (!isPending && !challengesData)
  );
};

export const useShouldShowSectionsHeader = () => {
  const isDesktop = useIsDesktopBp();

  const shouldHideChallenges = useShouldHideChallenges();

  const isLoading = useIsLoyaltyPageLoading();

  return !isDesktop && !shouldHideChallenges && !isLoading;
};

export const useSections = (activeSection: Section, onPress: (section: Section) => void) => {
  const activeChallengesBadge = useActiveChallengesBadge();
  return [
    {
      id: 'challenges',
      title: 'Challenges',
      onPress: () => onPress('challenges'),
      isActive: activeSection === 'challenges',
      badge: activeChallengesBadge,
    },
    {
      id: 'offers',
      title: 'Offers',
      onPress: () => onPress('offers'),
      isActive: activeSection === 'offers',
    },
    {
      id: 'rewards',
      title: 'Rewards',
      onPress: () => onPress('rewards'),
      isActive: activeSection === 'rewards',
    },
  ];
};
